import React from 'react';
import {Section} from "../../../../../elements/section/Section";
import {ValidationResult} from "../../../../../elements/validator/component/ValidationComponent";
import {ValidatedInputField} from "../../../../../elements/validator/component/ValidatedInputField";
import {DependantValidatedInputField} from "../../../../../elements/validator/dependant/DependantValidatedInputField";
import {ValidatedSelectField} from "../../../../../elements/validator/component/ValidatedSelectField";
import {DefaultValidValidator, IntegerNumberValidator, NotEmptyStringValidator} from "../../../../../elements/validator/Validator";
import {DisabledField, isFieldDisabled} from "../../../assistantPreset";
import {VehiclePriceDiscount} from "./valuationComponents/VehiclePriceDiscount/VehiclePriceDiscount";
import {VehicleConditionDiscount} from "./valuationComponents/VehicleConditionDiscount/VehicleConditionDiscount";
import {VehicleServiceHistoryDiscount} from "./valuationComponents/VehicleServiceHistoryDiscount/VehicleServiceHistoryDiscount";
import {ModifierRules} from "./valuationComponents/modifierRules/ModifierRules";
import {useAppDispatch, useAppSelector} from "../../../../../../app/hooks";
import {
    updateSettings,
    addNewRetailPriceRule,
    updateRetailPriceRule,
    deleteRetailPriceRule,
    selectConditionMultipliers,
    selectServiceHistoryMultipliers,
    selectMinDeduction,
    selectMaxDeduction,
    selectValuationPriceType,
    selectValuationProvider,
    selectValuationServiceGroupSoftwareHash
} from "../toolSettingsSlice";
import {
    BotType,
    UpdateBotToolSettingsRequest,
    UpdateRetailPriceRuleRequest,
    UpdateSubtractionRequest,
    ValuationPriceType,
    ValuationProvider
} from "../../../../../../api/generated";
import {SelectOption} from "../../../../../elements/select/SelectDropdown";
import styles from "../ToolSettingsForm.module.css";

interface PartExchangeRulesProps {
    botType: BotType;
    updateValidationStatus: (status: ValidationResult<UpdateBotToolSettingsRequest>) => void;
    getMessageWrapper: (f: string) => string;
}

const valuationPriceTypeOptions: SelectOption<ValuationPriceType>[] = [
    {label: "Part Exchange", value: ValuationPriceType.PartExchange},
    {label: "Trade", value: ValuationPriceType.Trade},
    {label: "Retail", value: ValuationPriceType.Retail},
    {label: "Private", value: ValuationPriceType.Private}
];

const valuationProviderOptions: SelectOption<ValuationProvider>[] = [
    {label: "Autotrader", value: ValuationProvider.Autotrader},
    {label: "Keyloop Valuation Service", value: ValuationProvider.KeyloopValuationService}
];

export const PartExchangeRules: React.FC<PartExchangeRulesProps> = ({
    botType,
    updateValidationStatus,
    getMessageWrapper
}) => {
    const dispatch = useAppDispatch();
    const conditionMultipliers = useAppSelector(selectConditionMultipliers);
    const serviceHistoryMultipliers = useAppSelector(selectServiceHistoryMultipliers);
    const valuationProvider = useAppSelector(selectValuationProvider);
    const isKeyloopValuationProvider = valuationProvider === ValuationProvider.KeyloopValuationService;

    const isPriceDiscountDisabled = isFieldDisabled(DisabledField.VEHICLE_PRICE_DISCOUNT, botType) || isKeyloopValuationProvider;
    const isConditionDisabled = isFieldDisabled(DisabledField.VEHICLE_CONDITION_DISCOUNT, botType) || isKeyloopValuationProvider;
    const isServiceHistoryDisabled = isFieldDisabled(DisabledField.VEHICLE_SERVICE_HISTORY_DISCOUNT, botType) || isKeyloopValuationProvider;

    const handleAddRetailPriceRule = (previousRuleIndex: number) => {
        dispatch(addNewRetailPriceRule(previousRuleIndex));
    };

    const handleUpdateRetailPriceRule = (index: number, rule: UpdateRetailPriceRuleRequest) => {
        dispatch(updateRetailPriceRule({...rule, ruleIndex: index}));
    };

    const handleDeleteRetailPriceRule = (ruleIndex: number) => {
        dispatch(deleteRetailPriceRule(ruleIndex));
    };

    const handleUpdateConditionMultipliers = (fieldName: string, value: UpdateSubtractionRequest) => {
        const updatedMultipliers = {
            ...conditionMultipliers,
            [fieldName]: value
        };
        dispatch(updateSettings({conditionMultipliers: updatedMultipliers}));
    };

    const handleUpdateServiceHistoryMultipliers = (fieldName: string, value: UpdateSubtractionRequest) => {
        const updatedMultipliers = {
            ...serviceHistoryMultipliers,
            [fieldName]: value
        };
        dispatch(updateSettings({serviceHistoryMultipliers: updatedMultipliers}));
    };

    return (
        <Section title={"Part Exchange Rules"}>
            <span className={styles.description}>
                The following rules are used as defaults. You can edit these values or delete irrelevant.
            </span>
            <div className={styles.fieldsLineWithMargin}>
                <ValidatedSelectField<ValuationProvider, UpdateBotToolSettingsRequest>
                    required={false}
                    onChange={(v) => dispatch(updateSettings({valuationProvider: v}))}
                    updateValidationStatus={updateValidationStatus}
                    fieldName={'valuationProvider'}
                    selector={selectValuationProvider}
                    validator={DefaultValidValidator}
                    disabled={false}
                    label={'Valuation Provider'}
                    options={valuationProviderOptions}
                    getMessage={getMessageWrapper}
                    selectFirstOptionByDefault={false}
                />
                {isKeyloopValuationProvider && (
                    <ValidatedInputField<string, UpdateBotToolSettingsRequest>
                        onChange={(v) => dispatch(updateSettings({valuationServiceGroupSoftwareHash: v}))}
                        updateValidationStatus={updateValidationStatus}
                        fieldName={'valuationServiceGroupSoftwareHash'}
                        selector={selectValuationServiceGroupSoftwareHash}
                        validator={NotEmptyStringValidator}
                        disabled={false}
                        label={'Valuation Service Group Software Hash'}
                        placeholder={"Group software hash"}
                        getMessage={getMessageWrapper}
                    />
                )}
            </div>
            <div
                className={`${styles.customValuationRules} ${isKeyloopValuationProvider ? styles.customValuationRulesDisabled : ''}`}
                aria-disabled={isKeyloopValuationProvider}
            >
                {isKeyloopValuationProvider && (
                    <div className={styles.customValuationRulesNotice}>
                        Custom PX rules are not applied for Keyloop Valuation Service
                    </div>
                )}
                <div className={styles.fieldsLineWithMargin}>
                    <ValidatedSelectField<ValuationPriceType, UpdateBotToolSettingsRequest>
                        required={false}
                        onChange={(v) => dispatch(updateSettings({valuationPriceType: v}))}
                        updateValidationStatus={updateValidationStatus}
                        fieldName={'valuationPriceType'}
                        selector={selectValuationPriceType}
                        validator={DefaultValidValidator}
                        disabled={isPriceDiscountDisabled}
                        label={'Valuation Price Type'}
                        options={valuationPriceTypeOptions}
                        getMessage={getMessageWrapper}
                        selectFirstOptionByDefault={false}
                    />
                </div>
                <div className={styles.fieldsLineWithMargin}>
                    <DependantValidatedInputField<number, UpdateBotToolSettingsRequest>
                        onChange={(v) => dispatch(updateSettings({minDeduction: v}))}
                        updateValidationStatus={updateValidationStatus}
                        fieldName={'minDeduction'}
                        selector={selectMinDeduction}
                        validator={IntegerNumberValidator}
                        disabled={isPriceDiscountDisabled}
                        label={'Min Discount'}
                        placeholder={"2000"}
                        getMessage={getMessageWrapper}
                        anchorValueSelector={(state) => state.toolSettings.updateRequest.botPublicId}
                    />
                    <DependantValidatedInputField<number, UpdateBotToolSettingsRequest>
                        onChange={(v) => dispatch(updateSettings({maxDeduction: v}))}
                        updateValidationStatus={updateValidationStatus}
                        fieldName={'maxDeduction'}
                        selector={selectMaxDeduction}
                        validator={IntegerNumberValidator}
                        disabled={isPriceDiscountDisabled}
                        label={'Max Discount'}
                        placeholder={"10000"}
                        getMessage={getMessageWrapper}
                        anchorValueSelector={(state) => state.toolSettings.updateRequest.botPublicId}
                    />
                </div>

                <VehiclePriceDiscount
                    getMessage={getMessageWrapper}
                    updateValidationStatus={updateValidationStatus}
                    addNewRetailPriceRule={handleAddRetailPriceRule}
                    updateRetailPriceRule={handleUpdateRetailPriceRule}
                    deleteRetailPriceRule={handleDeleteRetailPriceRule}
                    disabled={isPriceDiscountDisabled}
                />

                <VehicleConditionDiscount
                    getMessage={getMessageWrapper}
                    updateValidationStatus={updateValidationStatus}
                    updateConditionMultipliers={handleUpdateConditionMultipliers}
                    disabled={isConditionDisabled}
                />

                <VehicleServiceHistoryDiscount
                    getMessage={getMessageWrapper}
                    updateValidationStatus={updateValidationStatus}
                    updateServiceHistoryMultipliers={handleUpdateServiceHistoryMultipliers}
                    disabled={isServiceHistoryDisabled}
                />

                <ModifierRules
                    disabled={isPriceDiscountDisabled}
                    updateValidationStatus={updateValidationStatus}
                    getMessageWrapper={getMessageWrapper}
                />
            </div>
        </Section>
    );
};
